import { Button, Color, Icon, PopUp, Spacing, Text, TextInput, UploadButton } from 'zens-storybook'
import React, { Suspense, startTransition, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import {
  useStore,
  Draft,
  ProductsFilter,
  StorageKey,
  ApiError,
  useProductMutation,
  ProductData,
  DataWithUpload,
} from 'zens-store'
import { useNumberParams, usePaginationParam, useSortParam, Link, useFilterParam } from 'zens-router'
import { useBreadcrumb } from '@dms/hook'

import { ProductListHeader, ProductTable } from '@dms/component'
import { debounce } from 'lodash'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    margin: Spacing.L,
  },
  header: {
    flexDirection: 'row',
  },
  search: {
    minWidth: 300,
    marginRight: Spacing.S,
  },
  upload: {
    marginRight: Spacing.S,
  },
  button: {
    borderColor: Color.darkRed,
  },
  errorTitle: {
    marginBottom: Spacing.S,
  },
})

const ScreenProductList = () => {
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<false | string>(false)
  const { message, storage } = useStore()
  const { t } = useTranslation()
  const [{ companyId }] = useNumberParams()
  const [pagination, setPagination] = usePaginationParam()
  const [sort, setSort] = useSortParam({ field: 'serial_number', direction: 'asc' })
  const data = storage.get(`${companyId}:${StorageKey.ProductFilter}`, { session: true })
  const defaultValue = data ? JSON.parse(data) : undefined
  const [filter, setFilter] = useFilterParam<ProductsFilter>(defaultValue)
  const [search, setSearch] = useState<string>('')
  const [filterSearch, setFilterSearch] = useState<string>(search)
  const debouncedSearch = useRef(debounce((value) => startTransition(() => setFilterSearch(value)), 300)).current
  const mutation = useProductMutation({ companyId })

  const breadcrumb = useBreadcrumb([
    {
      title: t('Devices'),
    },
  ])

  function updateSearch(value: string) {
    setSearch(value)
    setPagination({ page: 0, size: pagination.size })
    debouncedSearch(value)
  }

  function updateFilter(filter: ProductsFilter) {
    setFilter(filter)
    setPagination({ page: 0, size: pagination.size })
  }

  const upload = async (_value?: string, file?: File) => {
    setUploading(true)
    try {
      await mutation.upload(new Draft<DataWithUpload<ProductData>>({ id: NaN, upload: file }))
      setUploading(false)
      message.add({
        message: t('Products were added successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`PRODUCT: Product Excel upload failed: ${errorMessage}`)
      setUploading(false)
      message.add({
        message: `${t('Products could not be added')}. ${errorMessage}`,
        status: 'error',
        timeout: 6000,
        ...(fields?.file
          ? {
              button: {
                title: t('Show details'),
                onPress: () => setUploadError(fields.file),
                contentContainerStyle: styles.button,
                danger: true,
                size: 'S',
              },
            }
          : {}),
      })
    }
  }

  const Header = (
    <View style={styles.header}>
      <TextInput
        left="search"
        placeholder={t('Search product name')}
        right={
          search ? (
            <TouchableOpacity onPress={() => updateSearch('')} testID="ProductList.Search.Clear">
              <Icon name="close" size="S" />
            </TouchableOpacity>
          ) : undefined
        }
        value={search || ''}
        onChangeText={updateSearch}
        style={styles.search}
        testID="ProductList.Search"
      />
      <UploadButton
        onSelect={upload}
        light
        style={styles.upload}
        options={{
          accept: {
            'application/vnd.ms-excel': [],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
          },
        }}
        loading={uploading}
      />
      <Link href={`/companies/${companyId}/products/add`} asChild>
        <Button title={t('Add device')} left="plus" testID="ProductList.Add" />
      </Link>
    </View>
  )

  return (
    <View style={styles.container}>
      <Suspense fallback={<ProductListHeader.Skeleton />}>
        <ProductListHeader
          breadcrumb={breadcrumb}
          companyId={companyId}
          pagination={pagination}
          sort={sort}
          filter={{ ...filter, search: filterSearch }}
          accessory={Header}
        />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<ProductTable.Skeleton />}>
          <ProductTable
            companyId={companyId}
            pagination={pagination}
            onPaginationChange={setPagination}
            sort={sort}
            onSortChange={setSort}
            filter={{ ...filter, search: filterSearch }}
            onFilterChange={updateFilter}
          />
        </Suspense>
      </View>
      {uploadError && (
        <PopUp visible onClose={() => setUploadError(false)}>
          <Text level={4} style={styles.errorTitle}>
            {t('Following errors were found during import')}
          </Text>
          {uploadError.split(',').map((error) => (
            <Text key={error}>{error.trim()}</Text>
          ))}
        </PopUp>
      )}
    </View>
  )
}

export default ScreenProductList
